import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL16_DYNAMIC_SYMBOLS = Object.freeze({
  movePlatform1: { imagePath: "port/generated/levels_fg/level_16/movePlatform1.runtime.svg" },
  movePlatform2: { imagePath: "port/generated/levels_fg/level_16/movePlatform2.runtime.svg" },
  jumpPlatform1: { imagePath: "port/generated/levels_fg/level_16/jumpPlatform1.runtime.svg" },
  bluePlato: { imagePath: "port/generated/levels_fg/level_16/bluePlato.runtime.svg" },
  dropPlatform: { imagePath: "port/generated/levels_fg/level_16/dropPlatform.runtime.svg" },
  killRollBall: { imagePath: "port/generated/levels_fg/level_16/killRollBall.runtime.svg" },
  axe1: { imagePath: "port/generated/levels_fg/level_16/axe1.runtime.svg" },
});

const LEVEL16_DYNAMIC_DRAW_ORDER = Object.freeze([
  "movePlatform1",
  "movePlatform2",
  "killRollBall1",
  "bluePlato",
  "jumpPlatform1",
  "killRollBall2",
  "dropPlatform",
  "axe1",
]);

function drawDynamicImage(ctx, image, x, y, rotationDeg = 0) {
  if (!image?.complete) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, -400, -400);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  if (rotationDeg) {
    ctx.rotate((rotationDeg * Math.PI) / 180);
  }
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelSixteenRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_16/1.visual.runtime.svg";
      this.level16DynamicImages = Object.fromEntries(
        Object.entries(LEVEL16_DYNAMIC_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
    } else {
      this.level16DynamicImages = {};
    }

    this.createAllLevelBodies();
    this.movePlatform1Body = this.getBody("movePlatform1");
    this.movePlatform2Body = this.getBody("movePlatform2");
    this.jumpPlatform1Body = this.getBody("jumpPlatform1");
    this.bluePlatformBody = this.getBody("bluePlato");
    this.dropPlatformBody = this.getBody("dropPlatform");
    this.killRollBall1Body = this.getBody("killRollBall1");
    this.killRollBall2Body = this.getBody("killRollBall2");
    this.axe1Body = this.getBody("axe1");

    this.addPrismaticJoint(this.movePlatform1Body, this.ground, this.movePlatform1Body.GetWorldCenter(), new b2Vec2(1, 0));
    this.addPrismaticJoint(this.movePlatform2Body, this.ground, this.movePlatform2Body.GetWorldCenter(), new b2Vec2(1, 0));
    this.jumpPlatform1Joint = this.addPrismaticJoint(this.jumpPlatform1Body, this.ground, this.jumpPlatform1Body.GetWorldCenter(), new b2Vec2(0, 1), {
      lowerTranslation: 0,
      upperTranslation: 1,
      enableLimit: true,
      maxMotorForce: 0,
      motorSpeed: 200,
      enableMotor: true,
    });
    this.addRevoluteJoint(this.axe1Body, this.ground, new b2Vec2(2767 / 30, -60 / 30));
    this.movePlatform1BodyDirection = 1;
    this.movePlatform2BodyDirection = 1;
    this.killRollBall1Direction = 1;
    this.killRollBall2Direction = -1;
  }

  levelUpdate() {
    if (
      this.player.y > 1000
      || this.contactListener.playerContactBodies.indexOf(this.killRollBall1Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.killRollBall2Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.axe1Body) !== -1
    ) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    if (this.hitNamedObject("wrongWay")) {
      this.lastCheckNum = 0;
      FlashLevelBaseRuntime.levelFlags[16].isStrelka = true;
    }
    if (this.getDisplayLayout("movePlatform1").x < 457) {
      this.movePlatform1BodyDirection = 1;
    }
    if (this.getDisplayLayout("movePlatform1").x > 787) {
      this.movePlatform1BodyDirection = -1;
    }
    this.movePlatform1Body.SetLinearVelocity(new b2Vec2(3 * this.movePlatform1BodyDirection, 0));

    if (this.getDisplayLayout("movePlatform2").x < 999) {
      this.movePlatform2BodyDirection = 1;
    }
    if (this.getDisplayLayout("movePlatform2").x > 1350) {
      this.movePlatform2BodyDirection = -1;
    }
    this.movePlatform2Body.SetLinearVelocity(new b2Vec2(3 * this.movePlatform2BodyDirection, 0));

    this.jumpPlatform1Joint.SetMaxMotorForce(this.contactListener.playerContactBodies.indexOf(this.jumpPlatform1Body) !== -1 ? 250 : 0);

    if (this.hitNamedObject("blueCheck1") && this.bluePlatformBody) {
      this.destroyBody(this.bluePlatformBody);
      this.bluePlatformBody = null;
    }
    if (this.hitNamedObject("blueCheck2") && this.bluePlatformBody) {
      this.destroyBody(this.bluePlatformBody);
      this.bluePlatformBody = null;
    }
    if (this.hitNamedObject("blueCheck3") && this.bluePlatformBody) {
      this.destroyBody(this.bluePlatformBody);
      this.bluePlatformBody = null;
    }
    if (this.contactListener.playerContactBodies.indexOf(this.dropPlatformBody) !== -1) {
      this.destroyBody(this.dropPlatformBody);
      this.dropPlatformBody = this.createBodyFromLevelData("dropPlatform", { density: 3 });
      this.refreshNamedRects();
    }
    if (this.getDisplayLayout("killRollBall1").x < 1450) {
      this.killRollBall1Direction = 1;
    }
    if (this.getDisplayLayout("killRollBall1").x > 1750) {
      this.killRollBall1Direction = -1;
    }
    this.killRollBall1Body.SetAngularVelocity(5 * this.killRollBall1Direction);
    if (this.getDisplayLayout("killRollBall2").x < 2120) {
      this.killRollBall2Direction = 1;
    }
    if (this.getDisplayLayout("killRollBall2").x > 2495) {
      this.killRollBall2Direction = -1;
    }
    this.killRollBall2Body.SetAngularVelocity(5 * this.killRollBall2Direction);
  }

  drawLevel16Bodies(ctx) {
    for (const name of LEVEL16_DYNAMIC_DRAW_ORDER) {
      if (name === "bluePlato" && !this.bluePlatformBody) {
        continue;
      }
      if (name === "dropPlatform" && !this.dropPlatformBody) {
        continue;
      }

      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }

      const imageName = name.startsWith("killRollBall") ? "killRollBall" : name;
      drawDynamicImage(
        ctx,
        this.level16DynamicImages[imageName],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    this.drawLevel16Bodies(ctx);

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
